import { yup } from '@framework/validation/initYup.ts';
import { debug } from '../../../../utils/debug.ts';
import { type IComputeExpressionValueReference } from '../../compute/ComputeExpression.ts';
import { ComputeMath } from '../../compute/ComputeMath.ts';
import { ComputeExpressionSchema } from '../../compute/validation/ComputeExpressionSchema.ts';
import { Datatype } from '../../datatype/Datatype.ts';
import { ExpressionType } from '../../IExpression.ts';
import { createExpressionType } from '../../IExpressionType.ts';
import { type IReference } from '../../references/IReference.ts';
import { type IFunctionExpression } from './IFunctionExpression.ts';

const getFunctionReferences = (expression: IFunctionExpression) =>
  expression.value.value
    .filter(
      (i): i is IComputeExpressionValueReference =>
        i.type === 'compute-expression-reference',
    )
    .map(i => {
      if (i.attrs.reference == null) {
        console.warn(`[getFunctionReferences] reference is null`, i);
      }
      return i.attrs.reference;
    })
    .filter(r => r != null);

interface IComputeToken {
  name: string;
  reference: IReference;
}

interface ICompiledExpression {
  tokens: IComputeToken[];
  evalFn: { evaluate(scope?: any): any };
}

// Cache WeakMap: le espressioni sono referenzialmente stabili dal JSON di
// configurazione, quindi la chiave non impedisce il GC.
// Evita di ri-parsare la stringa matematica ad ogni evaluate (~24x speedup).
const compiledCache = new WeakMap<IFunctionExpression, ICompiledExpression>();

function getCompiledExpression(
  expression: IFunctionExpression,
): ICompiledExpression {
  let cached = compiledCache.get(expression);
  if (cached) return cached;

  const tokens: IComputeToken[] = [];
  let currentToken = 0;
  const compiled = expression.value.value
    .map(item => {
      switch (item.type) {
        case 'compute-expression-reference': {
          const name = `var${currentToken++}`;
          tokens.push({ name, reference: item.attrs.reference });
          return name;
        }

        case 'text':
          return item.text;
      }
    })
    .join('');

  const evalFn = ComputeMath.parse(compiled).compile();
  cached = { tokens, evalFn };
  compiledCache.set(expression, cached);
  return cached;
}

export const FunctionExpressionType = createExpressionType<IFunctionExpression>(
  {
    type: ExpressionType.Function,
    schema: yup
      .object({
        // biome-ignore format: <tbd>
        type: yup.string().exact(ExpressionType.Function).required().label('Tipo di espressione'),
        value: ComputeExpressionSchema.required().label('Funzione'),
      })
      .label('Funzione'),
    evaluate(expression, scope) {
      const { tokens, evalFn } = getCompiledExpression(expression);
      // logger.log(
      //   'FunctionExpressionType.evaluate',
      //   expression,
      //   'compiled',
      //   compiled,
      //   scope.getAllReferences()
      // );

      try {
        // Non possiamo utilizzare `scope.getAllReferences()` perché gli UUID
        // non sono nomi di variabili validi (contengono trattini).
        // Creiamo quindi un oggetto `variables` che mappa i nomi generati
        // (var0, var1, ecc.) ai valori reali ottenuti da `scope.getReference()`.
        const variables: Record<string, any> = {};
        const allowNulls = scope.math.allowsMathWithNulls;
        const considerNullsAsZero = scope.math.considerNullsAsZero;

        // biome-ignore lint/style/useForOf: performance-critical hot path
        for (let i = 0; i < tokens.length; i++) /* NOSONAR */ {
          const token = tokens[i];
          const reference = token.reference;

          // Solo per i riferimenti di tipo Number, se considerNullsAsZero è
          // true, consideriamo i null come 0. Utile per i calcoli.
          const value = scope.getReference(
            reference,
            reference.field.type === Datatype.Number && considerNullsAsZero
              ? 0
              : null,
          );

          // Se una variabile non è definita, non possiamo valutare l'espressione.
          if (!allowNulls && value == null) {
            return null;
          }

          variables[token.name] = value;
        }

        const result = evalFn.evaluate(variables);
        // biome-ignore format: <tbd>
        debug.log('expression.compute.evaluate', 'Calcolo eseguito', { result, variables });

        // Per evitare di avere risultati come 0.9999999999999999, arrotondiamo
        // il risultato alla 12esima cifra decimale (la precisione di JS è di 14
        // cifre)
        return ComputeMath.round(result, 12);
      } catch (e) {
        // biome-ignore format: <tbd>
        console.error('FunctionExpressionType.evaluate', e, { expression, scope });
        return null;
      }
    },
    getReferences(expression, collect) {
      return getFunctionReferences(expression);
    },
    toString(expression, toString) {
      return expression.value.raw;
    },
  },
);
